import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { adminUploadImage } from '@/lib/api';
import { toast } from 'sonner';
import { Upload, X, Loader2, ImagePlus } from 'lucide-react';

export function ImageUploader({ images = [], onChange }) {
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

    const handleFiles = async (files) => {
        if (!files || files.length === 0) return;
        setUploading(true);
        
        const newImages = [...images];
        let uploaded = 0;
        
        for (const file of Array.from(files)) {
            // Validate
            if (!file.type.startsWith('image/')) {
                toast.error(`${file.name} no es una imagen.`);
                continue;
            }
            if (file.size > 10 * 1024 * 1024) {
                toast.error(`${file.name} es demasiado grande (max 10MB).`);
                continue;
            }
            
            try {
                const res = await adminUploadImage(file);
                const url = `${BACKEND_URL}${res.data.url}`;
                // Avoid duplicates
                if (!newImages.includes(url)) {
                    newImages.push(url);
                    uploaded++;
                }
            } catch (err) {
                toast.error(`Error subiendo ${file.name}`);
            }
        }
        
        if (uploaded > 0) {
            onChange(newImages);
            toast.success(`${uploaded} imagen${uploaded > 1 ? 'es' : ''} subida${uploaded > 1 ? 's' : ''}`);
        }
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const removeImage = (index) => {
        const newImages = images.filter((_, i) => i !== index);
        onChange(newImages);
    };

    const moveImage = (from, to) => {
        if (to < 0 || to >= images.length) return;
        const newImages = [...images];
        const [moved] = newImages.splice(from, 1);
        newImages.splice(to, 0, moved);
        onChange(newImages);
    };

    return (
        <div data-testid="image-uploader">
            {/* Image grid */}
            {images.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
                    {images.map((img, i) => (
                        <div key={i} className="relative group aspect-[4/3] rounded-lg overflow-hidden border bg-[var(--brand-gray-soft)]">
                            <img src={img} alt={`Foto ${i + 1}`} className="w-full h-full object-cover" />
                            {i === 0 && (
                                <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-[var(--brand-navy)] text-white">
                                    Principal
                                </span>
                            )}
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-1">
                                {i > 0 && (
                                    <button onClick={() => moveImage(i, i - 1)} className="h-7 w-7 rounded-full bg-white/80 flex items-center justify-center text-xs font-bold hover:bg-white">
                                        ←
                                    </button>
                                )}
                                <button onClick={() => removeImage(i)} className="h-7 w-7 rounded-full bg-red-500/80 text-white flex items-center justify-center hover:bg-red-500">
                                    <X className="h-3.5 w-3.5" />
                                </button>
                                {i < images.length - 1 && (
                                    <button onClick={() => moveImage(i, i + 1)} className="h-7 w-7 rounded-full bg-white/80 flex items-center justify-center text-xs font-bold hover:bg-white">
                                        →
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Upload button */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
            />
            <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
                className="w-full gap-2 border-dashed border-2 h-10"
            >
                {uploading ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Subiendo...</>
                ) : (
                    <><ImagePlus className="h-4 w-4" /> Subir fotos ({images.length})</>
                )}
            </Button>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1.5">
                JPG, PNG o WebP. Máximo 10MB por imagen. La primera foto será la principal.
            </p>
        </div>
    );
}
